import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DemoModal from "./DemoModal";
import RegisterModal from "./RegisterModal";

const waveSteps = [0, 30, 55, 80, 45, 65, 35, 70, 50, 85, 40, 60, 25, 75, 55, 90, 35, 65, 45, 70];

export default function HeroSection() {
  const { t } = useTranslation();
  const [waveAnim, setWaveAnim] = useState(false);
  const [activeBar, setActiveBar] = useState(0);
  const [callActive] = useState(true);
  const [showDemo, setShowDemo] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setWaveAnim(true), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!callActive) return;
    const interval = setInterval(() => {
      setActiveBar((p) => (p + 1) % waveSteps.length);
    }, 120);
    return () => clearInterval(interval);
  }, [callActive]);

  return (
    <>
      {showDemo && <DemoModal onClose={() => setShowDemo(false)} />}
      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onOpenDemo={() => setShowDemo(true)}
        />
      )}

      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(https://public.readdy.ai/ai/img_res/b5ae60b697cbbfc81c93db69781044bd.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/75" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-20 flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 backdrop-blur-sm mb-8">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-amber-300 text-xs font-semibold tracking-widest uppercase">
              {t("hero_badge")}
            </span>
          </div>

          <h1 className="text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl">
            {t("hero_title_1")}
            <br />
            <span
              className="text-amber-400"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
            >
              {t("hero_title_2")}
            </span>
          </h1>

          <p className="mt-8 text-xl lg:text-2xl text-white/70 max-w-2xl leading-relaxed font-light">
            {t("hero_subtitle").split(t("hero_subtitle_highlight"))[0]}
            <span className="text-white/90 font-medium">{t("hero_subtitle_highlight")}</span>
            {t("hero_subtitle").split(t("hero_subtitle_highlight"))[1]}
          </p>

          <div className="mt-12 w-full max-w-xl">
            <div className="bg-white/8 backdrop-blur-xl border border-white/15 rounded-3xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-400/20 border border-amber-400/30">
                      <i className="ri-robot-2-line text-amber-400 text-lg" />
                    </div>
                    <div className="text-left">
                      <p className="text-white text-sm font-semibold">{t("hero_ai_name")}</p>
                      <p className="text-white/50 text-xs">{t("hero_ai_company")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-emerald-300 text-xs font-medium">{t("hero_status_active")}</span>
                  </div>
                </div>

                <div className="flex items-end justify-center gap-1 h-14 mb-4">
                  {waveSteps.map((h, i) => (
                    <div
                      key={i}
                      className="rounded-full transition-all duration-100"
                      style={{
                        width: "6px",
                        height: waveAnim ? `${i === activeBar ? h + 15 : h * 0.6 + 5}px` : "4px",
                        backgroundColor:
                          i === activeBar
                            ? "#F59E0B"
                            : i % 2 === 0
                            ? "rgba(255,255,255,0.4)"
                            : "rgba(255,255,255,0.2)",
                        transition: "height 0.1s ease",
                      }}
                    />
                  ))}
                </div>

                <div className="bg-white/5 rounded-2xl p-3 mb-3">
                  <p className="text-white/80 text-sm text-left leading-relaxed">
                    <span className="text-amber-300 font-medium">IA:</span>{" "}
                    {t("hero_ai_greeting")}
                  </p>
                </div>
                <div className="bg-amber-400/10 rounded-2xl p-3 border border-amber-400/20">
                  <p className="text-white/80 text-sm text-left leading-relaxed">
                    <span className="text-white/50 font-medium">User:</span>{" "}
                    {t("hero_user_message")}
                  </p>
                </div>

                <div className="mt-3 flex items-center justify-between text-xs text-white/40">
                  <span>{t("hero_checking_calendar")}</span>
                  <span>FastMCP ✓</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
            <button
              onClick={() => setShowDemo(true)}
              className="flex items-center gap-2.5 px-8 py-4 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base"
            >
              <i className="ri-play-circle-line text-xl" />
              {t("hero_cta_demo")}
            </button>
            <button
              onClick={() => setShowRegister(true)}
              className="flex items-center gap-2.5 px-8 py-4 border border-white/30 hover:border-white/60 text-white font-semibold rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base backdrop-blur-sm"
            >
              <i className="ri-user-add-line text-xl" />
              {t("hero_cta_register")}
            </button>
          </div>

          <p className="mt-4 text-white/40 text-sm">{t("hero_disclaimer")}</p>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 w-full max-w-3xl">
            {[
              { val: "2.4M+", lbl: t("hero_stat_calls") },
              { val: "99.7%", lbl: t("hero_stat_uptime") },
              { val: "80%", lbl: t("hero_stat_savings") },
              { val: "31", lbl: t("hero_stat_languages") },
            ].map((s) => (
              <div key={s.val} className="text-center">
                <p className="text-2xl lg:text-3xl font-bold text-amber-400">{s.val}</p>
                <p className="text-white/50 text-xs mt-1">{s.lbl}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/30 text-xs tracking-widest uppercase">
            {t("hero_scroll_hint")}
          </span>
          <i className="ri-arrow-down-line text-white/30 text-lg" />
        </div>
      </section>
    </>
  );
}
